import { useEffect, useState } from 'react';
import { talentApi } from '../../../api/talent.js';
import { hrApi } from '../../../api/hr.js';

/**
 * Talent pools — curated cohorts (e.g. high-potentials) + membership.
 */
export default function Pools() {
  const [pools, setPools] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  function refresh() {
    setLoading(true);
    Promise.all([talentApi.listPools(), hrApi.listEmployees().catch(() => ({ items: [] }))])
      .then(([p, e]) => {
        setPools(p?.items || []);
        setEmployees(e?.items || e || []);
      })
      .catch((e) => setError(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }
  useEffect(refresh, []);

  async function openPool(poolId) {
    try {
      setSelected(await talentApi.getPool(poolId));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load pool');
    }
  }

  async function createPool(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await talentApi.createPool({ name: fd.get('name'), description: fd.get('description') || null });
      e.target.reset();
      setShowCreate(false);
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create pool');
    }
  }

  async function addMember(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await talentApi.addMember(selected.pool.id, fd.get('employeeId'));
      e.target.reset();
      openPool(selected.pool.id);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add member');
    }
  }

  return (
    <section data-testid="talent-pools-page" data-loading={loading ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Talent Pools</h1>
          <p className="muted">{pools.length} pools</p>
        </div>
        <button className="btn primary" data-testid="talent-pool-create" onClick={() => setShowCreate((s) => !s)}>
          {showCreate ? 'Close' : '+ Pool'}
        </button>
      </header>

      {error && <div className="alert error" data-testid="talent-pools-error">{error}</div>}

      {showCreate && (
        <form onSubmit={createPool} className="card form-card grid-form">
          <label>Name<input name="name" data-testid="talent-pool-name-input" required /></label>
          <label className="full">Description<input name="description" /></label>
          <div className="full actions"><button className="btn primary" type="submit" data-testid="talent-pool-submit">Create pool</button></div>
        </form>
      )}

      <div className="card">
        <table className="data-table" data-testid="talent-pool-list">
          <thead><tr><th>Pool</th><th>Description</th><th></th></tr></thead>
          <tbody>
            {pools.map((p) => (
              <tr key={p.id} data-testid={`talent-pool-row-${p.id}`}>
                <td>{p.name}</td>
                <td>{p.description || '—'}</td>
                <td><button className="btn ghost" data-testid={`talent-pool-open-${p.id}`} onClick={() => openPool(p.id)}>View members</button></td>
              </tr>
            ))}
            {!loading && pools.length === 0 && <tr><td colSpan={3} className="muted">No pools yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="card" data-testid="talent-pool-detail">
          <h3>{selected.pool.name} — members</h3>
          <ul data-testid="talent-pool-member-list">
            {selected.members.map((m) => <li key={m.id} data-testid={`talent-pool-member-${m.id}`}>{m.employee_name || m.employee_id}</li>)}
            {selected.members.length === 0 && <li className="muted">No members yet.</li>}
          </ul>
          <form onSubmit={addMember} className="form-card grid-form">
            <label>Employee
              <select name="employeeId" data-testid="talent-pool-member-input" required defaultValue="">
                <option value="" disabled>— select —</option>
                {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.user_name || emp.employee_code || emp.id}</option>)}
              </select>
            </label>
            <div className="actions"><button className="btn primary" type="submit" data-testid="talent-pool-member-add">Add member</button></div>
          </form>
        </div>
      )}
    </section>
  );
}
